import { useQuery } from '@tanstack/react-query';
import { fetchCandidateHistory } from '../api/candidateHistory';
import ModalShell from './ModalShell';

const TYPE_STYLES = {
  registered: 'bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))] border-emerald-200',
  profile: 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] border-[hsl(var(--ds-border))]',
  application: 'bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))] border-[hsl(var(--ds-primary)/0.25)]',
  stage: 'bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))] border-[hsl(var(--ds-primary)/0.25)]',
  rejected: 'bg-[hsl(var(--ds-danger)/0.12)] text-[hsl(var(--ds-danger))] border-red-200',
  interview: 'bg-[hsl(var(--ds-warning)/0.15)] text-[hsl(var(--ds-warning-fg))] border-amber-200',
  offer: 'bg-purple-100 text-purple-800 border-purple-200',
  email: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  notification: 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))] border-[hsl(var(--ds-border))]'
};

function isRejectedEvent(ev) {
  if (!ev) return false;
  if (ev.type === 'rejected' || ev.meta?.stage === 'rejected') return true;
  const hay = `${ev.title || ''} ${ev.detail || ''}`.toLowerCase();
  return /\brejected\b/.test(hay) || /\brejection\b/.test(hay);
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch {
    return iso;
  }
}

export default function CandidateHistoryModal({ candidateId, candidateName, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['candidate-history', candidateId],
    queryFn: () => fetchCandidateHistory(candidateId),
    enabled: Boolean(candidateId)
  });

  const events = data?.events || [];

  return (
    <ModalShell
      title="Candidate history"
      subtitle={candidateName || data?.candidateName || candidateId}
      onClose={onClose}
      maxWidth="max-w-2xl"
      zIndex="z-[15000]"
      footer={
        <button type="button" className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))] rounded-xl hover:bg-[hsl(var(--ds-muted))]" onClick={onClose}>
          Close
        </button>
      }
    >
      {isLoading && <p className="text-sm text-[hsl(var(--ds-muted-fg))]">Loading activity…</p>}
      {isError && (
        <p className="text-sm text-red-600">
          {error?.response?.data?.message || error?.message || 'Could not load history'}
        </p>
      )}
      {!isLoading && !isError && events.length === 0 && (
        <p className="text-sm text-[hsl(var(--ds-muted-fg))]">No recorded activity yet.</p>
      )}
      {!isLoading && !isError && events.length > 0 && (
        <ul className="space-y-3">
          {events.map(ev => {
            const rejected = isRejectedEvent(ev);
            const badgeStyle = rejected
              ? TYPE_STYLES.rejected
              : TYPE_STYLES[ev.type] || TYPE_STYLES.notification;

            return (
              <li
                key={ev.id}
                className={`flex gap-3 p-3 border rounded-xl ${
                  rejected
                    ? 'border-red-200 bg-red-50/70'
                    : 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/50'
                }`}
              >
                <div className="shrink-0 pt-0.5">
                  <span
                    className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide border rounded-xl ${badgeStyle}`}
                  >
                    {rejected ? 'rejected' : (ev.type || 'event').replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div
                    className={`font-medium text-sm ${
                      rejected ? 'text-red-800' : 'text-[hsl(var(--ds-fg))]'
                    }`}
                  >
                    {ev.title}
                  </div>
                  {ev.detail && (
                    <p
                      className={`text-xs mt-0.5 break-words ${
                        rejected ? 'text-red-700' : 'text-[hsl(var(--ds-muted-fg))]'
                      }`}
                    >
                      {ev.detail}
                    </p>
                  )}
                  <time
                    className={`text-[11px] mt-1 block ${
                      rejected ? 'text-red-500/80' : 'text-[hsl(var(--ds-muted-fg))]'
                    }`}
                  >
                    {formatWhen(ev.at)}
                  </time>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </ModalShell>
  );
}
